import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Zap, Loader2, CheckCircle2, XCircle, MonitorSmartphone } from 'lucide-react';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import LoginView from './LoginView.jsx';

/**
 * Approving the desktop notifier.
 *
 * The desktop app never asks for a password. It opens this page with a short
 * code, and the browser — which already holds the session, and which is the
 * only window an agent can check the address bar of — is what vouches for it.
 *
 * The page is declared alongside the emailed-link routes, which are rendered
 * in *both* the signed-in and signed-out branches of the router. That is what
 * lets it render the ordinary sign-in form below when there is no session: the
 * URL never changes, so signing in re-renders straight into the approval
 * screen with the code still in hand, instead of dumping the agent on /chat
 * with the request abandoned.
 */
export function LinkDeviceView() {
  const { status } = useAuth();
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';

  const [grant, setGrant] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | ready | approved | denied | error
  const [error, setError] = useState(null);

  const signedIn = status === 'authenticated';

  useEffect(() => {
    if (!signedIn || !code) return;
    let cancelled = false;

    api.device
      .preview(code)
      .then((data) => {
        if (cancelled) return;
        setGrant(data);
        // A grant approved on another tab or another browser is finished, not
        // waiting — offering the button again would only produce an error.
        setPhase(data.status === 'approved' ? 'approved' : 'ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setPhase('error');
      });

    return () => {
      cancelled = true;
    };
  }, [signedIn, code]);

  const decide = useCallback(
    async (approve) => {
      setError(null);
      try {
        await (approve ? api.device.approve(code) : api.device.deny(code));
        setPhase(approve ? 'approved' : 'denied');
      } catch (err) {
        setError(err.message);
      }
    },
    [code]
  );

  // No session yet: the sign-in form, at this same URL.
  if (!signedIn) return <LoginView />;

  if (!code) {
    return (
      <Frame title="Nothing to connect">
        <p className="login__sub">
          This page connects a desktop app to your account, and it needs the code that app
          gave it. Open SuperChat Alerts and choose “Sign in with browser”.
        </p>
      </Frame>
    );
  }

  if (phase === 'loading') {
    return (
      <Frame title="Checking that code">
        <p className="login__sub">
          <Loader2 size={14} className="spin" /> One moment…
        </p>
      </Frame>
    );
  }

  if (phase === 'error') {
    return (
      <Frame title="That code did not work">
        <div className="login__error">
          <XCircle size={13} /> {error}
        </div>
        <p className="login__sub">
          Codes expire after ten minutes. Start again from the desktop app and it will issue a
          fresh one.
        </p>
      </Frame>
    );
  }

  if (phase === 'approved') {
    return (
      <Frame title="Connected">
        <div className="result result--ok">
          <CheckCircle2 size={13} /> {grant?.clientName ?? 'The app'} can now send you
          notifications.
        </div>
        <p className="login__sub">
          You can close this tab. The app signs itself in within a few seconds — nothing else
          to do here.
        </p>
      </Frame>
    );
  }

  if (phase === 'denied') {
    return (
      <Frame title="Request refused">
        <p className="login__sub">
          Nothing was connected. If you did not start this yourself, no action is needed — the
          code grants nothing without this page.
        </p>
      </Frame>
    );
  }

  return (
    <Frame title="Connect this app?">
      <div className="result">
        <MonitorSmartphone size={13} /> {grant.clientName}
      </div>
      <p className="login__sub">
        It will be able to read your conversations and notify you about them, as if it were
        this console. Approve it only if you just started this from that computer.
      </p>
      <dl className="login__facts">
        <dt>Code</dt>
        <dd>{grant.userCode}</dd>
        <dt>Asked from</dt>
        <dd>{grant.requestedIp || 'an unknown address'}</dd>
        <dt>Requested</dt>
        <dd>{new Date(grant.requestedAt).toLocaleTimeString()}</dd>
      </dl>

      {error && (
        <div className="login__error">
          <XCircle size={13} /> {error}
        </div>
      )}

      <button type="button" className="button button--primary" onClick={() => decide(true)}>
        Connect it
      </button>
      <button type="button" className="button" onClick={() => decide(false)}>
        No, I did not start this
      </button>
    </Frame>
  );
}

function Frame({ title, children }) {
  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <Zap size={17} className="brand__bolt" />
          <span className="brand__name">Superchat</span>
        </div>
        <h1 className="login__title">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default LinkDeviceView;
